import React from 'react';
import { Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { thousand, toCurrency } from '../../../functions';

// The quality block that comes back with every result. Surfacing it is not
// optional: zeroCostRevenuePct is revenue whose cost is missing, so any margin
// shown above it is overstated by exactly that much. A margin dashboard that
// hides this is quietly wrong.
function QualityNote({ quality, className }) {
  const { t } = useTranslation();
  if (!quality) return null;

  const unmapped = Number(quality.unmappedRevenuePct) || 0;
  const zeroCost = Number(quality.zeroCostRevenuePct) || 0;
  const unmappedHigh = unmapped > 3;
  const zeroCostHigh = zeroCost > 2;

  const Item = ({ high, label, value, detail, tip }) => (
    <Tooltip styles={{ root: { maxWidth: 'none' } }} title={tip}>
      <span className="group inline-flex cursor-help items-center gap-1.5">
        <FontAwesomeIcon
          icon={high ? faTriangleExclamation : faCircleInfo}
          className={high ? 'text-warning' : 'text-muted'}
          style={{ fontSize: '0.75em' }}
        />
        <span className="text-muted">{label}</span>
        <span className={`font-semibold tabular-nums ${high ? 'text-warning' : 'text-ink'}`}>
          {value}
        </span>
        {detail && <span className="text-muted tabular-nums">({detail})</span>}
      </span>
    </Tooltip>
  );

  return (
    <div className={`${className || ''} flex flex-wrap items-center gap-x-6 gap-y-2 text-xs`}>
      <Item
        high={unmappedHigh}
        label={t('analytics_unmapped_revenue')}
        value={`${unmapped.toFixed(1)}%`}
        detail={toCurrency(quality.unmappedRevenue)}
        tip={t('analytics_unmapped_help')}
      />
      <Item
        high={zeroCostHigh}
        label={t('analytics_zero_cost_revenue')}
        value={`${zeroCost.toFixed(1)}%`}
        detail={toCurrency(quality.zeroCostRevenue)}
        tip={t('analytics_zero_cost_help')}
      />
      {quality.zeroCostMovementCount > 0 && (
        <Item
          high={false}
          label={t('analytics_zero_cost_movements')}
          value={thousand(quality.zeroCostMovementCount)}
          tip={t('analytics_zero_cost_movements_help')}
        />
      )}
      {quality.dateRangeClamped && (
        <span className="inline-flex items-center gap-1.5 font-semibold text-warning">
          <FontAwesomeIcon icon={faTriangleExclamation} style={{ fontSize: '0.75em' }} />
          {t('analytics_range_clamped')}
        </span>
      )}
    </div>
  );
}

export default QualityNote;
